import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getBookings, getBookingCount } from '../data/bookingStorage';
import { formatDate } from '../data/dateLocale';
import { Award, Ticket, User, Shield, CreditCard, Gift, Settings, ChevronRight, Info } from 'lucide-react';

const TABS = [
  { id: 'loyalty', key: 'account.loyalty', icon: Award },
  { id: 'trips', key: 'account.trips', icon: Ticket },
  { id: 'profile', key: 'account.profile', icon: User },
  { id: 'payment', key: 'account.paymentMethods', icon: CreditCard },
  { id: 'vouchers', key: 'account.vouchers', icon: Gift },
  { id: 'security', key: 'account.security', icon: Shield },
  { id: 'settings', key: 'account.settings', icon: Settings },
];

/* ── Loyalty ── */
const Loyalty = ({ user, t }) => {
  const count = getBookingCount(user.email);
  const needed = Math.max(0, 10 - count);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm flex items-center gap-4">
          <div className="w-14 h-14 rounded-full border-2 border-[#c30909] flex items-center justify-center text-[#c30909] font-bold text-xl">{count}</div>
          <div>
            <p className="text-sm font-bold">{t('account.tripsCount')}</p>
            <p className="text-xs text-[#777]">{t('account.year')}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm flex items-center gap-4">
          <div className="w-14 h-14 rounded-full border-2 border-[#c30909] flex items-center justify-center text-[#c30909] font-bold text-xl">{needed}</div>
          <div>
            <p className="text-sm font-bold">{t('account.tripsNeeded')}</p>
            <p className="text-xs text-[#777]">{t('account.forFreeTicket')}</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl p-5 shadow-sm space-y-3">
        <div className="flex items-start gap-3">
          <Info size={16} className="text-[#484c55]/40 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-[#555] leading-relaxed italic">{t('account.loyaltyInfo1')}</p>
        </div>
        <div className="flex items-start gap-3">
          <Info size={16} className="text-[#484c55]/40 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-[#555] leading-relaxed italic">{t('account.loyaltyInfo2')}</p>
        </div>
      </div>
    </div>
  );
};

/* ── Trips ── */
const Trips = ({ user, t, lang }) => {
  const [tab, setTab] = useState('upcoming');
  const bookings = getBookings(user.email);
  const navigate = useNavigate();
  const filtered = bookings.filter((b) => tab === 'upcoming' ? !b.isPast : b.isPast);

  return (
    <div>
      <div className="flex border-b mb-4 bg-white rounded-t-xl">
        {['upcoming', 'past'].map((id) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex-1 py-3 text-sm font-medium transition-colors relative ${tab === id ? 'text-[#484c55]' : 'text-[#484c55]/40'}`}
          >
            {t(`account.${id}`)}
            {tab === id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#c30909]" />}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-b-xl shadow-sm">
          <Ticket size={48} className="mx-auto text-[#484c55]/20 mb-4" />
          <p className="text-lg font-bold text-[#555] mb-2">{t('account.noTickets')}</p>
          <p className="text-sm text-[#484c55]/40 mb-6">{t('account.noTicketsDesc')}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-xs uppercase tracking-wider px-6 py-3 rounded-lg transition-colors"
          >
            {t('account.searchTrips')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <div key={b.id} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[#777]">#{b.id}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${b.isPast ? 'bg-gray-100 text-[#777]' : 'bg-[#2e9e5a]/10 text-[#2e9e5a]'}`}>
                  {b.isPast ? t('account.past') : t('account.upcoming')}
                </span>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#c30909]" />
                <span className="text-sm font-bold">{b.trip?.from} → {b.trip?.to}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-[#555]">
                <p>{formatDate(b.departureDate, lang)}</p>
                <p>{b.trip?.departureTime} — {b.trip?.arrivalTime}</p>
                <p>{b.passengerInfo?.firstName} {b.passengerInfo?.lastName}</p>
                <p className="font-bold text-[#484c55]">{b.totalPrice} €</p>
              </div>
              {b.returnTrip && (
                <div className="mt-2 pt-2 border-t flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#484c55]/30" />
                  <span className="text-xs text-[#555]">{b.returnTrip.from} → {b.returnTrip.to} · {formatDate(b.returnDate, lang)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Profile ── */
const Profile = ({ user, t, updateProfile }) => {
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [gender, setGender] = useState(user.gender || '');
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateProfile({ firstName, lastName, phone, gender });
    setSaved(true);
    setEditing(false);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold">{t('account.profile')}</h2>
        {!editing && (
          <button onClick={() => setEditing(true)} className="text-xs text-[#c30909] hover:underline">✎</button>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Avatar */}
        <div className="flex-shrink-0 flex justify-center">
          <div className="w-24 h-24 rounded-full bg-[#c9a96e]/30 flex items-center justify-center">
            <User size={36} className="text-[#c9a96e]" />
          </div>
        </div>

        {/* Fields */}
        <div className="flex-1 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('auth.email')}</label>
            <input type="email" value={user.email} readOnly className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm bg-gray-50 text-[#777]" />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="gender" value="male" checked={gender === 'male'} onChange={(e) => setGender(e.target.value)} disabled={!editing} className="accent-[#c30909]" />
              {t('account.male')}
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="gender" value="female" checked={gender === 'female'} onChange={(e) => setGender(e.target.value)} disabled={!editing} className="accent-[#c30909]" />
              {t('account.female')}
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('passenger.firstName')} *</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} readOnly={!editing} className={`w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none ${editing ? 'focus:border-[#c30909]' : 'bg-gray-50'}`} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('passenger.lastName')} *</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} readOnly={!editing} className={`w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none ${editing ? 'focus:border-[#c30909]' : 'bg-gray-50'}`} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('passenger.phone')} *</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} readOnly={!editing} placeholder="+371 00 000 000" className={`w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none ${editing ? 'focus:border-[#c30909]' : 'bg-gray-50'}`} />
          </div>
          {editing && (
            <div className="flex gap-3">
              <button onClick={handleSave} className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-sm uppercase tracking-wider px-6 py-3 rounded-lg transition-colors">
                {t('account.save')}
              </button>
              <button onClick={() => setEditing(false)} className="text-sm text-[#777] hover:text-[#484c55] px-4 py-3">
                {t('search.cancel') || 'Отмена'}
              </button>
            </div>
          )}
          {saved && <p className="text-xs text-[#2e9e5a] font-medium">{t('account.saved')}</p>}
        </div>
      </div>
    </div>
  );
};

/* ── Payment Methods ── */
const PaymentMethods = ({ t }) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-bold mb-6">{t('account.paymentMethods')}</h2>
      <div className="text-center py-12">
        <CreditCard size={48} className="mx-auto text-[#484c55]/20 mb-4" />
        <p className="text-lg font-bold text-[#555] mb-2">{t('account.noPaymentMethods')}</p>
        <p className="text-sm text-[#484c55]/40 mb-6">{t('account.noPaymentMethodsDesc')}</p>
        <button className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-xs uppercase tracking-wider px-6 py-3 rounded-lg transition-colors">
          {t('account.addCard')}
        </button>
      </div>
    </div>
  );
};

/* ── Vouchers ── */
const Vouchers = ({ t }) => {
  const [code, setCode] = useState('');
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-bold mb-6">{t('account.vouchers')}</h2>
      <div className="flex gap-2 mb-8">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('account.enterVoucher')}
          className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909]"
        />
        <button className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-xs uppercase tracking-wider px-6 py-3 rounded-lg transition-colors">
          {t('account.activate')}
        </button>
      </div>
      <div className="text-center py-8">
        <Gift size={48} className="mx-auto text-[#484c55]/20 mb-4" />
        <p className="text-lg font-bold text-[#555] mb-2">{t('account.noVouchers')}</p>
        <p className="text-sm text-[#484c55]/40">{t('account.noVouchersDesc')}</p>
      </div>
    </div>
  );
};

/* ── Security ── */
const Security = ({ user, t, updateProfile }) => {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');

  const handleChange = () => {
    if (current !== user.password) { setMsg(t('auth.wrongCredentials')); return; }
    if (newPw !== confirm) { setMsg(t('auth.passwordMismatch')); return; }
    if (newPw.length < 4) return;
    updateProfile({ password: newPw });
    setCurrent(''); setNewPw(''); setConfirm('');
    setMsg(t('account.passwordChanged'));
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <h2 className="text-lg font-bold mb-6">{t('account.security')}</h2>
      <div className="space-y-4 max-w-sm">
        <div>
          <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('account.currentPassword')}</label>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('account.newPassword')}</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#777] uppercase tracking-wider mb-1.5">{t('auth.confirmPassword')}</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#c30909]" />
        </div>
        {msg && <p className={`text-xs font-medium ${msg === t('account.passwordChanged') ? 'text-[#2e9e5a]' : 'text-[#c30909]'}`}>{msg}</p>}
        <button onClick={handleChange} className="bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-sm uppercase tracking-wider px-6 py-3 rounded-lg transition-colors">
          {t('account.changePassword')}
        </button>
      </div>
    </div>
  );
};

/* ── Settings ── */
const SettingsSection = ({ t, lang }) => {
  const [notifications, setNotifications] = useState(true);
  const [newsletter, setNewsletter] = useState(false);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-bold mb-6">{t('account.settings')}</h2>
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{t('account.language')}</p>
              <p className="text-xs text-[#777]">{lang.toUpperCase()}</p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{t('account.currency')}</p>
              <p className="text-xs text-[#777]">EUR (€)</p>
            </div>
          </div>
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium">{t('account.notifications')}</p>
              <button
                onClick={() => setNotifications(!notifications)}
                className={`w-10 h-6 rounded-full transition-colors relative ${notifications ? 'bg-[#c30909]' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${notifications ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{t('account.newsletter')}</p>
              <button
                onClick={() => setNewsletter(!newsletter)}
                className={`w-10 h-6 rounded-full transition-colors relative ${newsletter ? 'bg-[#c30909]' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${newsletter ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <button className="text-sm text-[#c30909] hover:underline">{t('account.deleteAccount')}</button>
      </div>
    </div>
  );
};

/* ── Main Account Page ── */
const AccountPage = () => {
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useAuth();
  const { t, lang } = useLanguage();
  const [activeTab, setActiveTab] = useState('loyalty');

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  if (!user) return null;

  const initials = `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase();

  const renderSection = () => {
    switch (activeTab) {
      case 'loyalty': return <Loyalty user={user} t={t} lang={lang} />;
      case 'trips': return <Trips user={user} t={t} lang={lang} />;
      case 'profile': return <Profile user={user} t={t} updateProfile={updateProfile} />;
      case 'payment': return <PaymentMethods t={t} />;
      case 'vouchers': return <Vouchers t={t} />;
      case 'security': return <Security user={user} t={t} updateProfile={updateProfile} />;
      case 'settings': return <SettingsSection t={t} lang={lang} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <div className="max-w-[1280px] mx-auto px-3 md:px-4 py-6 md:py-8">
        <h1 className="text-xl md:text-2xl font-bold text-[#484c55] mb-4 md:mb-6">{t('account.myAccount')}</h1>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          {/* Sidebar */}
          <div className="w-full md:w-[260px] flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* User info */}
              <div className="p-4 border-b flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#c9a96e] flex items-center justify-center text-white text-sm font-bold">{initials}</div>
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{user.firstName} {user.lastName}</p>
                  <p className="text-xs text-[#777] truncate">{user.email}</p>
                </div>
              </div>

              {/* Nav — horizontal scroll on mobile, vertical on desktop */}
              <div className="flex md:flex-col overflow-x-auto md:overflow-visible scrollbar-hide">
                {TABS.map(({ id, key, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-l-0 md:border-l-2 border-b-2 md:border-b-0 ${
                      activeTab === id
                        ? 'text-[#c30909] md:border-l-[#c30909] border-b-[#c30909] bg-[#c30909]/5'
                        : 'text-[#484c55]/70 border-transparent hover:text-[#484c55] hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    <span className="hidden md:inline">{t(key)}</span>
                    <span className="md:hidden text-xs">{t(key)}</span>
                    {activeTab === id && <ChevronRight size={14} className="ml-auto hidden md:block text-[#c30909]" />}
                  </button>
                ))}
              </div>

              {/* Logout */}
              <div className="border-t p-3 hidden md:block">
                <button
                  onClick={() => { logout(); navigate('/'); }}
                  className="text-xs text-[#777] hover:text-[#c30909] transition-colors w-full text-left px-1"
                >
                  {t('auth.logout')}
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
